// brr is the snes's sample format: 16 samples in 9 bytes (a header and 16 four bit steps). the game converter does its own
// compression, so this exists for two things: the "hear it like the snes" preview and the raw .brr export.
// samples are handled in the chip's 15 bit range, so an 8 bit sample is multiplied by 128

const FILTER_TERMS: Array<(p1: number, p2: number) => number> = [
  () => 0,
  (p1) => p1 + ((-p1) >> 4),
  (p1, p2) => (p1 << 1) + ((-(p1 * 3)) >> 5) - p2 + (p2 >> 4),
  (p1, p2) => (p1 << 1) + ((-(p1 * 13)) >> 6) - p2 + ((p2 * 3) >> 4)
];

function wrap15(value: number): number {
  const clipped = Math.min(32767, Math.max(-32768, value));
  return ((clipped << 17) >> 17);
}

// one four bit step turned into a sample, given the two before it
function step(nibble: number, range: number, filter: number, p1: number, p2: number): number {
  const base = range <= 12 ? (nibble << range) >> 1 : nibble < 0 ? -2048 : 0;
  return wrap15(base + FILTER_TERMS[filter](p1, p2));
}

export function encodeBrr(samples: Int8Array, loopFrom = -1): Uint8Array {
  const blocks = Math.ceil(samples.length / 16);
  const out = new Uint8Array(blocks * 9);
  let p1 = 0;
  let p2 = 0;
  for (let block = 0; block < blocks; block++) {
    const target = new Int32Array(16);
    for (let i = 0; i < 16; i++) target[i] = (samples[block * 16 + i] || 0) * 128;
    // the block a loop jumps back to starts from silence, so it has to be filter 0
    const filters = block === 0 || block === loopFrom ? [0] : [0, 1, 2, 3];
    let best = { error: Infinity, range: 0, filter: 0, nibbles: new Int8Array(16), p1: 0, p2: 0 };
    for (const filter of filters) {
      for (let range = 0; range <= 12; range++) {
        let a = p1;
        let b = p2;
        let error = 0;
        const nibbles = new Int8Array(16);
        for (let i = 0; i < 16; i++) {
          const predicted = FILTER_TERMS[filter](a, b);
          const nibble = Math.min(7, Math.max(-8, Math.round(((target[i] - predicted) * 2) / 2 ** range)));
          const got = step(nibble, range, filter, a, b);
          error += (target[i] - got) ** 2;
          nibbles[i] = nibble;
          b = a;
          a = got;
        }
        if (error < best.error) best = { error, range, filter, nibbles, p1: a, p2: b };
      }
    }
    const last = block === blocks - 1;
    out[block * 9] = (best.range << 4) | (best.filter << 2) | (last && loopFrom >= 0 ? 2 : 0) | (last ? 1 : 0);
    for (let i = 0; i < 8; i++) out[block * 9 + 1 + i] = ((best.nibbles[i * 2] & 15) << 4) | (best.nibbles[i * 2 + 1] & 15);
    p1 = best.p1;
    p2 = best.p2;
  }
  return out;
}

// back to 16 bit samples, what the chip would play
export function decodeBrr(bytes: Uint8Array): Int16Array {
  const blocks = Math.floor(bytes.length / 9);
  const out = new Int16Array(blocks * 16);
  let p1 = 0;
  let p2 = 0;
  for (let block = 0; block < blocks; block++) {
    const header = bytes[block * 9];
    const range = header >> 4;
    const filter = (header >> 2) & 3;
    for (let i = 0; i < 16; i++) {
      const byte = bytes[block * 9 + 1 + (i >> 1)];
      const raw = i % 2 === 0 ? byte >> 4 : byte & 15;
      const got = step(raw > 7 ? raw - 16 : raw, range, filter, p1, p2);
      out[block * 16 + i] = got * 2;
      p2 = p1;
      p1 = got;
    }
  }
  return out;
}
